import { useState } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import {
  ChevronLeft,
  ExternalLink,
  RefreshCw,
  Image,
  History,
  TrendingUp,
  Pencil,
  Database,
  Activity,
  Shield,
  Check,
} from 'lucide-react';
import { fetchBacklink, fetchBacklinkHistory, checkBacklink, updateBacklink, refreshBacklinkMetrics, markBacklinkStatus } from '../api/backlinks';
import { refreshDFSMetrics } from '../api/dataforseo';
import StatusBadge from '../components/StatusBadge';
import LinkVitals from '../components/LinkVitals';
import Lightbox from '../components/ui/Lightbox';
import Modal from '../components/Modal';
import { formatRelativeDate, formatDateWithTooltip } from '../utils/dateUtils';
import { cn, getDAColor, getSpamScoreColor, getQualityGrade, qualityGradeConfig, trustLevelConfig, calculatePBNScore } from '../lib/utils';
import { PageHeader } from '../components/ui/PageHeader';
import { AnimatedCard, CardHeader, CardTitle, CardContent } from '../components/ui/AnimatedCard';
import { Button, IconButton } from '../components/ui/Button';

function BacklinkDetail() {
  const { id } = useParams();
  const location = useLocation();
  const queryClient = useQueryClient();

  // Preserve the search params from the referrer (if coming from project detail page)
  const getBackToProjectUrl = (projectId) => {
    // Check if we have state with preserved filters
    if (location.state?.filters) {
      const params = new URLSearchParams();
      if (location.state.filters.status) params.set('status', location.state.filters.status);
      if (location.state.filters.search) params.set('search', location.state.filters.search);
      if (location.state.filters.page && location.state.filters.page > 1) params.set('page', location.state.filters.page.toString());
      const queryString = params.toString();
      return queryString ? `/projects/${projectId}?${queryString}` : `/projects/${projectId}`;
    }
    return `/projects/${projectId}`;
  };

  const { data: backlinkData, isLoading } = useQuery({
    queryKey: ['backlink', id],
    queryFn: () => fetchBacklink(id),
  });

  const { data: historyData } = useQuery({
    queryKey: ['backlink-history', id],
    queryFn: () => fetchBacklinkHistory(id),
  });

  const checkMutation = useMutation({
    mutationFn: checkBacklink,
    onSuccess: () => {
      queryClient.invalidateQueries(['backlink', id]);
      queryClient.invalidateQueries(['backlink-history', id]);
      toast.success('Check completed');
    },
    onError: (error) => {
      const message = error.response?.data?.message || error.response?.data?.details || 'Check failed';
      toast.error(message);
    },
  });

  // Manual override — when the crawler reports a false positive (e.g. host site
  // blocks our crawler IP and the link reports as timeout/removed) but the user
  // has verified the link is live by hand.
  const markStatusMutation = useMutation({
    mutationFn: ({ status, notes }) => markBacklinkStatus(id, status, notes),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries(['backlink', id]);
      queryClient.invalidateQueries(['backlink-history', id]);
      toast.success(`Marked as ${variables.status}`);
    },
    onError: (error) => {
      const message = error.response?.data?.message || 'Failed to update status';
      toast.error(message);
    },
  });

  const refreshMetricsMutation = useMutation({
    mutationFn: () => refreshBacklinkMetrics(id),
    onSuccess: (data) => {
      queryClient.invalidateQueries(['backlink', id]);
      toast.success('Moz metrics updated successfully');
    },
    onError: (error) => {
      const message = error.response?.data?.message || 'Failed to refresh Moz metrics';
      toast.error(message);
    },
  });

  const refreshDFSMutation = useMutation({
    mutationFn: () => refreshDFSMetrics(id),
    onSuccess: (data) => {
      queryClient.invalidateQueries(['backlink', id]);
      toast.success('DataForSEO metrics updated successfully');
    },
    onError: (error) => {
      const data = error.response?.data;
      const status = error.response?.status;
      const code = data?.code || 'UNKNOWN';
      const message = data?.message || 'Failed to refresh DataForSEO metrics';
      console.error(`[DFS Refresh] HTTP ${status}, code=${code}`, data);
      toast.error(`${message} (${code})`);
    },
  });

  // Lightbox state
  const [lightbox, setLightbox] = useState({ isOpen: false, src: '', title: '' });
  const openLightbox = (src, title) => setLightbox({ isOpen: true, src, title });
  const closeLightbox = () => setLightbox({ isOpen: false, src: '', title: '' });

  // Edit backlink state
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    target_url: '',
    expected_anchor: '',
    tags: '',
  });

  const updateMutation = useMutation({
    mutationFn: (data) => updateBacklink(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['backlink', id]);
      setIsEditModalOpen(false);
      toast.success('Backlink updated successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to update backlink');
    },
  });

  const handleEditClick = () => {
    if (backlinkData?.backlink) {
      setEditForm({
        target_url: backlinkData.backlink.target_url || '',
        expected_anchor: backlinkData.backlink.expected_anchor || '',
        tags: backlinkData.backlink.tags || '',
      });
      setIsEditModalOpen(true);
    }
  };

  const handleEditSubmit = (e) => {
    e.preventDefault();
    updateMutation.mutate(editForm);
  };

  const backlink = backlinkData?.backlink;
  const history = historyData?.history || [];

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-48 bg-muted rounded animate-pulse" />
        <div className="h-64 bg-muted rounded-xl animate-pulse" />
      </div>
    );
  }

  if (!backlink) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Backlink not found</p>
        <Link to="/projects" className="text-primary hover:text-primary/80 mt-4 inline-block">
          Return to projects
        </Link>
      </div>
    );
  }

  const qualityGrade = getQualityGrade(parseFloat(backlink.quality_score));
  const gradeConfig = qualityGrade ? qualityGradeConfig[qualityGrade] : null;
  const isDfsAbsent = backlink.dfs_rank == null && (!backlink.dfs_referring_domains || parseInt(backlink.dfs_referring_domains) === 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <Link
            to={getBackToProjectUrl(backlink.project_id)}
            className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-2 transition-colors"
          >
            <ChevronLeft className="h-4 w-4" />
            Back to Project
          </Link>
          <h1 className="text-xl font-bold text-foreground break-all">
            {backlink.backlink_url}
          </h1>
          <div className="flex items-center gap-4 mt-2">
            <StatusBadge status={backlink.status} />
            <span className="text-muted-foreground text-sm" title={backlink.last_checked ? formatDateWithTooltip(backlink.last_checked).absolute : ''}>
              Last checked: {backlink.last_checked
                ? formatRelativeDate(backlink.last_checked)
                : 'Never'}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={handleEditClick}
            variant="secondary"
            leftIcon={<Pencil className="h-4 w-4" />}
          >
            Edit
          </Button>
          <Button
            onClick={() => checkMutation.mutate(id)}
            disabled={checkMutation.isPending}
            loading={checkMutation.isPending}
            leftIcon={!checkMutation.isPending && <RefreshCw className="h-4 w-4" />}
          >
            Check Now
          </Button>
          {backlink.status !== 'live' && (
            <Button
              onClick={() => {
                if (window.confirm(`Mark this backlink as live?\n\nUse this when you've manually verified the link is alive but the crawler keeps reporting it as ${backlink.status}. The status will reset to "live", consecutive failures will clear, and a manual_override entry will be added to the history.`)) {
                  markStatusMutation.mutate({ status: 'live', notes: `Manual override from status=${backlink.status}` });
                }
              }}
              disabled={markStatusMutation.isPending}
              loading={markStatusMutation.isPending}
              variant="outline"
              leftIcon={!markStatusMutation.isPending && <Check className="h-4 w-4" />}
              title="Manually mark this backlink as live (use when you've verified the crawler is wrong)"
            >
              Mark as Live
            </Button>
          )}
        </div>
      </div>

      {/* Info Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* URLs Card */}
          <AnimatedCard>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ExternalLink className="h-5 w-5 text-primary" />
                Link Details
              </CardTitle>
            </CardHeader>
            <CardContent>
              <dl className="space-y-4">
                <div>
                  <dt className="text-sm text-muted-foreground">Backlink URL</dt>
                  <dd className="text-foreground break-all">
                    <a
                      href={backlink.backlink_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:text-primary transition-colors"
                    >
                      {backlink.backlink_url}
                    </a>
                  </dd>
                </div>
                <div>
                  <dt className="text-sm text-muted-foreground">Target URL</dt>
                  <dd className="text-foreground break-all">
                    <a
                      href={backlink.target_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:text-primary transition-colors"
                    >
                      {backlink.target_url}
                    </a>
                  </dd>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <dt className="text-sm text-muted-foreground">Link Type</dt>
                    <dd className="text-foreground capitalize">{backlink.link_type || 'Unknown'}</dd>
                  </div>
                  <div>
                    <dt className="text-sm text-muted-foreground">HTTP Status</dt>
                    <dd className="text-foreground">{backlink.http_status || '-'}</dd>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <dt className="text-sm text-muted-foreground">Expected Anchor</dt>
                    <dd className="text-foreground">{backlink.expected_anchor || '-'}</dd>
                  </div>
                  <div>
                    <dt className="text-sm text-muted-foreground">Found Anchor</dt>
                    <dd className="text-foreground">{backlink.anchor_text_found || '-'}</dd>
                  </div>
                </div>
                {backlink.tags && (
                  <div>
                    <dt className="text-sm text-muted-foreground">Tags</dt>
                    <dd className="flex flex-wrap gap-2 mt-1">
                      {backlink.tags.split(',').map((tag, i) => (
                        <span
                          key={i}
                          className="px-2 py-1 text-xs bg-secondary rounded-full text-secondary-foreground"
                        >
                          {tag.trim()}
                        </span>
                      ))}
                    </dd>
                  </div>
                )}
              </dl>
            </CardContent>
          </AnimatedCard>

          {/* Check History */}
          <AnimatedCard>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <History className="h-5 w-5 text-primary" />
                Check History
              </CardTitle>
            </CardHeader>
            <CardContent>
              {history.length > 0 ? (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {history.map((check) => (
                    <div
                      key={check.id}
                      className="flex items-center gap-4 p-3 rounded-lg bg-muted/50"
                    >
                      <StatusBadge status={check.status} />
                      <div className="flex-1">
                        <p className="text-sm text-foreground">
                          {check.link_found ? 'Link found' : 'Link not found'}
                          {check.link_type_found && ` (${check.link_type_found})`}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          HTTP {check.http_status} • {check.response_ms}ms
                        </p>
                      </div>
                      <span className="text-xs text-muted-foreground" title={formatDateWithTooltip(check.checked_at).absolute}>
                        {formatRelativeDate(check.checked_at)}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground text-center py-8">
                  No check history yet
                </p>
              )}
            </CardContent>
          </AnimatedCard>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Moz Metrics Card */}
          <AnimatedCard>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="flex items-center gap-2 text-base">
                <TrendingUp className="h-5 w-5 text-primary" />
                Moz Metrics
              </CardTitle>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => refreshMetricsMutation.mutate()}
                disabled={refreshMetricsMutation.isPending}
                loading={refreshMetricsMutation.isPending}
                leftIcon={!refreshMetricsMutation.isPending && <RefreshCw className="h-3 w-3" />}
              >
                {refreshMetricsMutation.isPending ? 'Loading...' : 'Refresh'}
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-muted-foreground" title="Domain Authority (Moz, 0-100): Measures overall domain strength. Higher = more authoritative.">Domain Authority</span>
                  <span className={cn('font-medium', getDAColor(backlink.domain_authority))}>
                    {backlink.domain_authority || '-'}
                  </span>
                </div>
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <div
                    className={cn('h-full rounded-full transition-all',
                      backlink.domain_authority >= 50 ? 'bg-success' :
                      backlink.domain_authority >= 30 ? 'bg-warning' : 'bg-primary'
                    )}
                    style={{ width: `${backlink.domain_authority || 0}%` }}
                  />
                </div>
              </div>
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-muted-foreground" title="Page Authority (Moz, 0-100): Measures specific page strength. Higher = more authoritative.">Page Authority</span>
                  <span className={cn('font-medium', getDAColor(backlink.page_authority))}>
                    {backlink.page_authority || '-'}
                  </span>
                </div>
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <div
                    className={cn('h-full rounded-full transition-all',
                      backlink.page_authority >= 50 ? 'bg-success' :
                      backlink.page_authority >= 30 ? 'bg-warning' : 'bg-primary'
                    )}
                    style={{ width: `${backlink.page_authority || 0}%` }}
                  />
                </div>
              </div>
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-muted-foreground" title="Moz Spam Score (0-17): Measures likelihood of spam. Based on 27 signals. Higher = riskier.">Spam Score</span>
                  <span className={cn('font-medium', getSpamScoreColor(backlink.spam_score, 17))}>
                    {backlink.spam_score != null ? `${backlink.spam_score}/17` : '-'}
                  </span>
                </div>
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <div
                    className={cn('h-full rounded-full transition-all',
                      (backlink.spam_score || 0) >= 10 ? 'bg-destructive' :
                      (backlink.spam_score || 0) >= 5 ? 'bg-warning' : 'bg-success'
                    )}
                    style={{ width: `${(backlink.spam_score || 0) / 17 * 100}%` }}
                  />
                </div>
              </div>
              <div className="pt-4 border-t border-border">
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Quality Score</span>
                  <div className="flex items-center gap-1.5">
                    <span className={cn('font-bold', gradeConfig?.color || 'text-muted-foreground')}>
                      {backlink.quality_score ? `${(backlink.quality_score * 100).toFixed(0)} (${gradeConfig?.label || ''})` : '-'}
                    </span>
                    {isDfsAbsent && backlink.quality_score && (
                      <span className="text-[10px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded" title="Based on Moz data only. Run DFS refresh for full analysis.">
                        Moz only
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </AnimatedCard>

          {/* DataForSEO Metrics Card */}
          <AnimatedCard>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="flex items-center gap-2 text-base">
                <Database className="h-5 w-5 text-info" />
                DataForSEO
              </CardTitle>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => refreshDFSMutation.mutate()}
                disabled={refreshDFSMutation.isPending}
                loading={refreshDFSMutation.isPending}
                leftIcon={!refreshDFSMutation.isPending && <RefreshCw className="h-3 w-3" />}
                title="Refresh DataForSEO metrics (~$0.02)"
              >
                {refreshDFSMutation.isPending ? 'Loading...' : 'Refresh'}
              </Button>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground" title="DataForSEO Domain Rank: Lower = better. Top sites rank under 10K. Based on backlink profile strength.">Domain Rank</span>
                <span className="text-foreground font-medium">
                  {backlink.dfs_rank?.toLocaleString() || '-'}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground" title="Total number of backlinks pointing to this domain (from DataForSEO).">Backlinks</span>
                <span className="text-foreground font-medium">
                  {backlink.dfs_backlinks_count?.toLocaleString() || '-'}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground" title="Number of unique domains linking to this domain. Higher diversity = more trustworthy.">Referring Domains</span>
                <span className="text-foreground font-medium">
                  {backlink.dfs_referring_domains?.toLocaleString() || '-'}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground" title="Estimated monthly organic search traffic to this domain (from DataForSEO).">Est. Traffic</span>
                <span className="text-foreground font-medium">
                  {backlink.traffic != null && backlink.traffic > 0 ? `${backlink.traffic.toLocaleString()}/mo` : '-'}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground" title="DataForSEO Spam Score (0-100): Measures spam risk. Higher = more likely spam.">DFS Spam Score</span>
                <span className={cn('font-medium', getSpamScoreColor(backlink.dfs_spam_score, 100))}>
                  {backlink.dfs_spam_score != null ? backlink.dfs_spam_score : '-'}
                </span>
              </div>
              {backlink.dfs_updated_at && (
                <div className="pt-3 border-t border-border">
                  <span className="text-xs text-muted-foreground">
                    Updated: {formatRelativeDate(backlink.dfs_updated_at)}
                  </span>
                </div>
              )}
            </CardContent>
          </AnimatedCard>

          {/* Link Vitals Card */}
          <AnimatedCard>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Activity className="h-5 w-5 text-primary" />
                Link Vitals
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Response Speed</span>
                <span className={cn('font-medium',
                  backlink.last_response_ms == null ? 'text-muted-foreground' :
                  backlink.last_response_ms < 1000 ? 'text-success' :
                  backlink.last_response_ms < 3000 ? 'text-warning' : 'text-destructive'
                )}>
                  {backlink.last_response_ms != null ? `${backlink.last_response_ms}ms` : 'N/A'}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Consecutive Failures</span>
                <span className={cn('font-medium',
                  (backlink.consecutive_failures || 0) === 0 ? 'text-success' :
                  (backlink.consecutive_failures || 0) <= 2 ? 'text-warning' : 'text-destructive'
                )}>
                  {backlink.consecutive_failures || 0}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Stability</span>
                <span className="text-foreground font-medium">
                  {backlink.last_status_change
                    ? `${Math.floor((Date.now() - new Date(backlink.last_status_change).getTime()) / (1000 * 60 * 60 * 24))} days since change`
                    : 'N/A'}
                </span>
              </div>
              <div className="pt-2">
                <LinkVitals
                  consecutiveFailures={backlink.consecutive_failures}
                  lastStatusChange={backlink.last_status_change}
                  lastResponseMs={backlink.last_response_ms}
                />
              </div>
            </CardContent>
          </AnimatedCard>

          {/* Trust Level Card */}
          <AnimatedCard>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Shield className="h-5 w-5 text-primary" />
                Trust Analysis
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {(() => {
                const { pbnScore, trustLevel, signals } = calculatePBNScore({
                  da: parseFloat(backlink.domain_authority) || 0,
                  traffic: parseInt(backlink.traffic) || 0,
                  referringDomains: parseInt(backlink.dfs_referring_domains) || 0,
                  referringIps: parseInt(backlink.dfs_referring_ips) || 0,
                  backlinksCount: parseInt(backlink.dfs_backlinks_count) || 0,
                  spamScore: parseFloat(backlink.spam_score) || 0,
                  dfsSpamScore: backlink.dfs_spam_score != null ? parseFloat(backlink.dfs_spam_score) : null,
                });
                const tConfig = trustLevelConfig[trustLevel];
                // Detect trust/quality mismatch for user explanation
                const qualityLabel = gradeConfig?.label;
                const isMismatch = (trustLevel === 'trusted' && qualityLabel !== 'Strong')
                  || (trustLevel === 'suspicious' && qualityLabel === 'Strong');
                return (
                  <>
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground text-sm">Trust Level</span>
                      <span className={cn('px-2 py-0.5 rounded-full text-xs font-semibold', tConfig.bg, tConfig.color)}>
                        {tConfig.label}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">PBN Score</span>
                      <span className="text-foreground font-medium">{pbnScore}/5</span>
                    </div>
                    {signals.length > 0 && (
                      <div>
                        <span className="text-xs text-muted-foreground">Signals:</span>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {signals.map((s, i) => (
                            <span key={i} className="px-1.5 py-0.5 text-[10px] bg-warning/15 text-warning rounded">
                              {s}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {isMismatch && (
                      <p className="text-[11px] text-muted-foreground pt-2 border-t border-border/50">
                        Trust measures PBN risk (spam signals). Quality measures overall value (authority, stability, diversity).
                        {isDfsAbsent && ' Quality may improve after running DFS refresh.'}
                      </p>
                    )}
                  </>
                );
              })()}
            </CardContent>
          </AnimatedCard>

          {/* Link Evidence Card */}
          {(backlink.html_snippet || backlink.link_position || backlink.canonical_url || backlink.target_url_found) && (
            <AnimatedCard>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <ExternalLink className="h-5 w-5 text-info" />
                  Link Evidence
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {backlink.link_position && backlink.link_position !== 'unknown' && (
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-muted-foreground">Position</span>
                    <span className={cn('px-2 py-0.5 rounded-full text-xs font-medium',
                      backlink.link_position === 'content' ? 'bg-success/20 text-success' :
                      backlink.link_position === 'sidebar' ? 'bg-warning/20 text-warning' :
                      backlink.link_position === 'footer' ? 'bg-muted text-muted-foreground' :
                      'bg-info/20 text-info'
                    )}>
                      {backlink.link_position}
                    </span>
                  </div>
                )}
                {backlink.target_url_found && (
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-muted-foreground">Actual href</span>
                    <a href={backlink.target_url_found} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline truncate max-w-[200px]" title={backlink.target_url_found}>
                      {backlink.target_url_found.length > 40 ? backlink.target_url_found.substring(0, 40) + '...' : backlink.target_url_found}
                    </a>
                  </div>
                )}
                {backlink.canonical_url && (
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-muted-foreground">Canonical URL</span>
                    <span className="text-foreground truncate max-w-[200px]" title={backlink.canonical_url}>
                      {backlink.canonical_url.length > 40 ? backlink.canonical_url.substring(0, 40) + '...' : backlink.canonical_url}
                    </span>
                  </div>
                )}
                {backlink.outbound_link_count != null && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Outbound Links</span>
                    <span className="text-foreground font-medium">{backlink.outbound_link_count}</span>
                  </div>
                )}
                {backlink.html_snippet && (
                  <div>
                    <span className="text-xs text-muted-foreground block mb-1">HTML Snippet</span>
                    <pre className="text-xs bg-muted/50 rounded-lg p-3 overflow-x-auto text-foreground/80 whitespace-pre-wrap break-all border border-border/50">
                      {backlink.html_snippet}
                    </pre>
                  </div>
                )}
              </CardContent>
            </AnimatedCard>
          )}

          {/* Score Breakdown Card */}
          {backlink.quality_score != null && (
            <AnimatedCard>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <TrendingUp className="h-5 w-5 text-primary" />
                  Score Breakdown
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {(() => {
                  const da = parseFloat(backlink.domain_authority) || 0;
                  const spam = parseFloat(backlink.spam_score) || 0;
                  const dfsRank = parseInt(backlink.dfs_rank) || 0;
                  const refDomains = parseInt(backlink.dfs_referring_domains) || 0;
                  const traffic = parseInt(backlink.traffic) || 0;
                  const pbn = parseInt(backlink.pbn_score) || 0;
                  const qs = parseFloat(backlink.quality_score) || 0;

                  const factors = [
                    { label: 'DA (Moz)', weight: '25%', raw: da.toFixed(0), contribution: Math.min(da / 100, 1) * 25, max: 25 },
                    { label: 'Spam Score', weight: '20%', raw: spam.toFixed(0) + '%', contribution: Math.max(0, (1 - spam / 100)) * 20, max: 20 },
                    { label: 'DFS Rank', weight: '15%', raw: dfsRank > 0 ? dfsRank.toLocaleString() : 'N/A', contribution: dfsRank > 0 ? Math.min(Math.log10(dfsRank + 1) / 6, 1) * 15 : 7.5, max: 15 },
                    { label: 'Referring Domains', weight: '15%', raw: refDomains > 0 ? refDomains.toLocaleString() : 'N/A', contribution: refDomains > 0 ? Math.min(refDomains / 500, 1) * 15 : 0, max: 15 },
                    { label: 'Traffic', weight: '15%', raw: traffic > 0 ? traffic.toLocaleString() : 'N/A', contribution: traffic > 0 ? Math.min(traffic / 10000, 1) * 15 : 0, max: 15 },
                    { label: 'Stability', weight: '10%', raw: backlink.consecutive_failures === 0 ? 'Stable' : `${backlink.consecutive_failures} failures`, contribution: backlink.consecutive_failures === 0 ? 10 : Math.max(0, 10 - backlink.consecutive_failures * 3), max: 10 },
                  ];

                  return (
                    <>
                      <div className="flex items-center justify-between mb-2 pb-2 border-b border-border">
                        <span className="text-sm font-medium text-foreground">Overall Score</span>
                        <span className={cn('text-lg font-bold', gradeConfig?.color || 'text-foreground')}>{(qs * 100).toFixed(0)}</span>
                      </div>
                      {factors.map((f, i) => (
                        <div key={i} className="space-y-0.5">
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-muted-foreground">{f.label} <span className="text-muted-foreground/50">({f.weight})</span></span>
                            <span className="text-foreground tabular-nums">{f.raw}</span>
                          </div>
                          <div className="h-1.5 bg-muted/30 rounded-full overflow-hidden">
                            <div className="h-full rounded-full bg-primary/70" style={{ width: `${(f.contribution / f.max) * 100}%` }} />
                          </div>
                        </div>
                      ))}
                      {pbn >= 3 && (
                        <div className="pt-2 border-t border-border mt-2">
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-destructive font-medium">PBN Penalty (x0.7)</span>
                            <span className="text-destructive">-{(qs * 100 * 0.3).toFixed(0)} pts</span>
                          </div>
                          <div className="h-1.5 bg-destructive/10 rounded-full overflow-hidden mt-1">
                            <div className="h-full rounded-full bg-destructive/50" style={{ width: '30%' }} />
                          </div>
                        </div>
                      )}
                      <p className="text-[11px] text-muted-foreground pt-2 border-t border-border/50 mt-2">
                        {refDomains < 50 && refDomains > 0 ? 'Needs 50+ referring domains for Strong rating. ' : ''}
                        {da < 30 ? 'Low DA — target higher authority sites. ' : ''}
                        {spam > 30 ? 'High spam score — review this domain. ' : ''}
                      </p>
                    </>
                  );
                })()}
              </CardContent>
            </AnimatedCard>
          )}

          {/* Screenshot Card */}
          <AnimatedCard>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Image className="h-5 w-5 text-primary" />
                Screenshot
              </CardTitle>
            </CardHeader>
            <CardContent>
              {backlink.screenshot_url ? (
                <button
                  onClick={() => openLightbox(backlink.screenshot_url, backlink.backlink_url)}
                  className="block w-full cursor-zoom-in"
                >
                  <img
                    src={backlink.screenshot_url}
                    alt="Page screenshot"
                    className="w-full rounded-lg border border-border hover:border-primary transition-colors"
                  />
                </button>
              ) : (
                <div className="flex items-center justify-center h-32 bg-muted rounded-lg text-muted-foreground">
                  No screenshot available
                </div>
              )}
            </CardContent>
          </AnimatedCard>
        </div>
      </div>

      {/* Screenshot Lightbox */}
      <Lightbox
        isOpen={lightbox.isOpen}
        onClose={closeLightbox}
        src={lightbox.src}
        title={lightbox.title}
      />

      {/* Edit Backlink Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        title="Edit Backlink"
      >
        <form onSubmit={handleEditSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              Target URL
            </label>
            <input
              type="url"
              value={editForm.target_url}
              onChange={(e) => setEditForm({ ...editForm, target_url: e.target.value })}
              className="w-full px-3 py-2 bg-muted border border-border rounded-lg text-foreground focus:ring-2 focus:ring-ring focus:border-ring transition-colors"
              placeholder="https://yoursite.com/landing-page"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              Expected Anchor Text
            </label>
            <input
              type="text"
              value={editForm.expected_anchor}
              onChange={(e) => setEditForm({ ...editForm, expected_anchor: e.target.value })}
              className="w-full px-3 py-2 bg-muted border border-border rounded-lg text-foreground focus:ring-2 focus:ring-ring focus:border-ring transition-colors"
              placeholder="Click here"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              Tags
            </label>
            <input
              type="text"
              value={editForm.tags}
              onChange={(e) => setEditForm({ ...editForm, tags: e.target.value })}
              className="w-full px-3 py-2 bg-muted border border-border rounded-lg text-foreground focus:ring-2 focus:ring-ring focus:border-ring transition-colors"
              placeholder="guest-post, tech, seo"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Comma-separated tags for organizing backlinks
            </p>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="ghost"
              onClick={() => setIsEditModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={updateMutation.isPending}
              loading={updateMutation.isPending}
            >
              {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

export default BacklinkDetail;
